import { css } from "@emotion/react";
import type { ExactTheme } from "src/lib/emotion/theme.ts";

export const rootContainer = css({
  display: "inline-flex",
  flexDirection: "column",
  width: "100%",
  minWidth: 200,
  height: "fit-content",
});

export const dropIconStyle = (theme: ExactTheme) =>
  css({ flexShrink: 0, isolation: "isolate", color: theme.colorsV2.grey[600] });

export const rootInputStyle =
  ({
    progress,
    isUploading,
    isDrag,
  }: {
    isUploading?: boolean;
    progress: number;
    isDrag: boolean;
  }) =>
  ({ colorsV2, fontWeights }: ExactTheme) =>
    css({
      position: "relative",
      display: "flex",
      alignItems: "center",
      padding: 15,
      fontWeight: fontWeights.semibold,

      ".progress": {
        position: "absolute",
        inset: 0,
        willChange: "transform, opacity",
        transition: "transform 200ms, opacity 200ms ease-out",
        backgroundColor: colorsV2.green[50],
        transform: `translateX(${progress - 100}%)`,
        opacity: isUploading && progress ? 0.5 : 0,
        zIndex: 0,
      },

      label: {
        position: "relative",
        height: "100%",
        width: "100%",
        textAlign: isUploading ? "center" : "start",
        alignContent: "center",
        padding: "0 10px",
        // whiteSpace: "nowrap",
        // overflow: "hidden",
        // textOverflow: "ellipsis",
        userSelect: "none",
      },

      ".dragOver": {
        position: "absolute",
        placeContent: "center",
        textAlign: "center",
        inset: 0,
        backgroundColor: colorsV2.grey[25],
        opacity: isDrag ? 1 : 0,
        willChange: "opacity",
        transition: "opacity 200ms",
        pointerEvents: "none",
      },
    });

export const rootDropZoneStyle =
  ({ error, disabled }: { error?: string; disabled?: boolean }) =>
  ({
    borderRadiusV2,
    fontWeights,
    fontSizesV2,
    colorsV2,
    inputBorderV2,
  }: ExactTheme) => {
    return css({
      position: "relative",
      display: "inline-flex",
      flexDirection: "column",
      width: "100%",
      minHeight: 80,
      borderRadius: borderRadiusV2.primary,
      overflow: "hidden",
      fontWeight: fontWeights.normal,
      fontSize: fontSizesV2.body,
      background: disabled ? colorsV2.grey[50] : colorsV2.white,
      pointerEvents: disabled ? "none" : "all",
      color: colorsV2.grey[800],
      outline: "none",

      border:
        inputBorderV2.dashed[disabled ? "disabled" : "standard"][
          error ? "error" : "default"
        ],

      ...(!disabled && {
        // ":hover": {
        //   border: inputBorderV2.dashed.hover[error ? "error" : "default"],
        // },

        ":focus-visible": {
          border: inputBorderV2.dashed.focused[error ? "error" : "default"],
          boxShadow: error
            ? `0 0 0 1px${colorsV2.red[500]}`
            : `0 0 0 1px${colorsV2.blue[500]}`,
        },
      }),
    });
  };

export const helperTextStyles =
  ({ error, dragNDropMode }: { error?: string; dragNDropMode?: boolean }) =>
  (theme: ExactTheme) =>
    css({
      height: 16,
      margin: 0,
      alignContent: "end",
      color: error ? theme.colorsV2.red[700] : theme.colorsV2.grey[600],
      fontSize: dragNDropMode
        ? theme.fontSizesV2.body
        : theme.fontSizesV2.body2,
    });

export const previewListStyle = ({ colorsV2 }: ExactTheme) =>
  css({
    display: "flex",
    flexDirection: "column",
    gap: 5,
    padding: 15,
    borderTop: `1px solid ${colorsV2.grey[100]}`,
  });

export const rootRowStyle = ({ colorsV2, inputBorderV2 }: ExactTheme) =>
  css({
    display: "flex",
    alignItems: "center",
    gap: 5,
    height: 55,
    padding: 10,
    borderRadius: 6,
    cursor: "pointer",
    border: inputBorderV2.transparent,
    willChange: "border, background",
    transition: "border 150ms, background 150ms",

    ":hover": {
      border: `1px solid ${colorsV2.grey[100]}`,
      backgroundColor: colorsV2.grey[25],
    },
  });

export const rowPlaceholder = css({
  padding: "10px",
  fontSize: "14px",
  marginRight: "auto",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  userSelect: "none",
});
